import type { CreateMultipleTradeUserResponse } from '~/pages/api/test/create-multiple-trade-user';
import type { CreateSoftPullUserResponse } from '~/pages/api/test/create-softpull-user';
import type { CreateTradeUserResponse } from '~/pages/api/test/create-trade-user';
import { getValidationToken } from './turnstile';

export type UserAccountType =
  | 'common'
  | 'expired-trade'
  | 'expired-softpull'
  | 'expired-soft-cosigner'
  | 'expired-soft-trade'
  | 'multiple-trades'
  | 'softpull'
  | 'soft-cosigner'
  | 'soft-trade'
  | 'tradein'
  | 'loan-amount'
  | 'update-user-trade-info';

export type TradeSlot = {
  type: 'active' | 'expired' | 'pending';
  vin?: string;
};

export type UserCreationOptions = {
  creditStatus?: 'Fair' | 'Excellent';
  equityLevel?: 'Positive' | 'High Positive' | 'Negative';
  lienHolder?: string;
  loanAmount?: number;
  name?: string;
  state?: string;
  trades?: TradeSlot[];
  vin?: string;
};

export type VehicleOption = {
  vin: string;
  label: string;
  description: string;
};

export const VEHICLE_OPTIONS: VehicleOption[] = [
  {
    vin: '2GNALBEKXF1102556',
    label: '2015 Chevrolet Equinox - $100',
    description: '2015 Chevrolet Equinox LT Sport Utility 4D'
  },
  {
    vin: 'JN1AZ4EH2AM504529',
    label: '2010 Nissan Altima',
    description: '2010 Nissan Altima (Positive Equity)'
  },
  {
    vin: '1GYS4EKL2PR191194',
    label: '2023 Cadillac Escalade',
    description: '2023 Cadillac Escalade (High Value)'
  },
  {
    vin: 'JHLRE3H41BC015693',
    label: '2011 Honda CR-V',
    description: '2011 Honda CR-V (Reliable SUV)'
  },
  {
    vin: '5YJSA1E44LF364113',
    label: '2020 Tesla Model S',
    description: '2020 Tesla Model S (Electric Luxury)'
  }
];

export type CreatedUser = {
  email: string;
  password: string;
  type: UserAccountType;
  createdAt: string;
  userId?: string;
  ssn?: string;
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  address?: {
    street: string;
    city: string;
    state: string;
    zip: string;
  };
  dateOfBirth?: string;
  income?: number;
  acquisitionId?: string;
  acquisitionIds?: string[];
  hasLoanAmount?: boolean;
  name?: string;
};

// Test personas used for softpull user creation
const SOFTPULL_FAIR_CREDIT = {
  address: {
    city: 'PAWTUCKET',
    state: 'RI',
    street: '115 TRENTON ST APT 4',
    zip: '02860'
  },
  dateOfBirth: '05291951',
  firstName: 'MARSHALL',
  income: 65342,
  lastName: 'BROYLES',
  phoneNumber: '2345678901',
  ssn: '666016662'
};

const SOFTPULL_EXCELLENT_CREDIT = {
  address: {
    city: 'CLIFTON PARK',
    state: 'NY',
    street: '49 SPICE MILL BLVD',
    zip: '12065'
  },
  dateOfBirth: '07101948',
  firstName: 'MARY',
  income: 65342,
  lastName: 'NALETILIC',
  phoneNumber: '8003712062',
  ssn: '666168711'
};

const SOFTPULL_COSIGNER = {
  address: {
    city: 'Staples',
    state: 'MN',
    street: '501 McKinley St SW',
    zip: '56479'
  },
  dateOfBirth: '10271943',
  firstName: 'Erin',
  income: 45000,
  lastName: 'Perkins',
  phoneNumber: '5557418956',
  ssn: '666594984',
  statedEmploymentLength: 'TWO_TO_FIVE_YEARS'
};

export const WSYS_SCENARIOS: Record<string, string> = {
  Control: 'JTJCHMAA2P2005829',
  'Incentive And Tax Savings': '1HGCY2F55RA097824',
  'Incentive No Tax Savings': 'KM8R44GE3PU592433',
  'Tax Savings': '4S4BTAFC9S3197903'
};

export type TradeOptions = {
  equityLevel: 'High Positive' | 'Negative' | 'Positive';
  loanAmount?: number;
  status: 'active' | 'expired' | 'pending';
  vin?: string;
};

const TEST_USERS_STORAGE_KEY = 'merch-ui-test-users';
const SESSION_USER_KEY = 'merch-ui-passenger-session-user';
const DAYS_TO_EXPIRE_TRADE = -8;

export function getCurrentSessionUser(): CreatedUser | null {
  const stored = sessionStorage.getItem(SESSION_USER_KEY);

  return stored ? (JSON.parse(stored) as CreatedUser) : null;
}

export function getTestUsersFromLocalStorage(): Record<string, CreatedUser> {
  const stored = localStorage.getItem(TEST_USERS_STORAGE_KEY);

  return stored ? JSON.parse(stored) : {};
}

export function clearTestUsersFromLocalStorage(): void {
  try {
    localStorage.removeItem(TEST_USERS_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear test users from localStorage:', error);
  }
}

export function storeTestUserInLocalStorage(user: CreatedUser): void {
  const existingUsers = getTestUsersFromLocalStorage();
  existingUsers[user.email] = user;
  localStorage.setItem(TEST_USERS_STORAGE_KEY, JSON.stringify(existingUsers));
}

export function setSessionUser(user: CreatedUser): void {
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
}

export async function copyToClipboard(text: string): Promise<void> {
  await navigator.clipboard.writeText(text);
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function toFormattedPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function toSoftPullDOB(dob: string): string {
  if (dob.includes('/')) {
    const [mm, dd, yyyy] = dob.split('/');

    return `${yyyy}-${mm}-${dd}`;
  }

  return `${dob.slice(4)}-${dob.slice(0, 2)}-${dob.slice(2, 4)}`;
}

// On localhost the Next.js dev server serves /tools/test/* API routes;
// on testazure those routes are 404 (feature branches are JS-only deploys).
function isLocalDev(): boolean {
  return window.location.hostname.includes('localhost');
}

// ─── Shared helpers (work on both localhost and testazure) ──────────────────

async function createUserAndGetId(
  email: string,
  password: string,
  zip: string,
  firstName = 'MARSHALL',
  lastName = 'BROYLES'
): Promise<string> {
  const createRes = await fetch('/v2/user-create', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      callerIdentifier: 'AccountCreationForm',
      confirmPassword: password,
      deliveryZip: zip,
      email,
      firstName,
      isAgreeToTermsAndConditions: true,
      isOptedInToTcpaConsent: true,
      lastName,
      partnerId: '1',
      password,
      phoneNumber: toFormattedPhone('2345678901')
    })
  });
  if (!createRes.ok)
    throw new Error(`Failed to create user: ${createRes.statusText}`);

  const loginRes = await fetch('/v2/user-login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ emailAddress: email, password })
  });
  if (!loginRes.ok) throw new Error(`Failed to login: ${loginRes.statusText}`);

  const loginBody = (await loginRes.json()) as {
    content?: { userId?: string };
  };
  const userId = loginBody.content?.userId;
  if (!userId) throw new Error('userId not found in login response');

  return userId;
}

// ─── Browser-direct helpers (testazure) ────────────────────────────────────

// Fetches a QE service token from apim for expire operations.
async function getQeServiceToken(): Promise<string> {
  const params = new URLSearchParams({
    client_id: 'qe-test-automation',
    client_secret: 'qe-test-automation',
    grant_type: 'client_credentials',
    scope: 'employee_profile qeService'
  });
  const res = await fetch(
    'https://apim.carvanatech.com/auth/identity/connect/token',
    {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body: params.toString()
    }
  );
  if (!res.ok)
    throw new Error(`Failed to get QE service token: ${res.statusText}`);

  const data = (await res.json()) as { access_token: string };

  return data.access_token;
}

async function expireSoftPullDirect(userId: string): Promise<void> {
  const token = await getQeServiceToken();
  const res = await fetch(
    `https://apps.carvanatech.com/qe/qeorchestration/api/v1/user/expiresoftpull?userId=${userId}`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      }
    }
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to expire softpull: ${res.status} ${res.statusText} — ${body}`
    );
  }
}

async function extendOrExpireTradeDirect(
  acquisitionId: string,
  daysToAdd: number
): Promise<void> {
  const token = await getQeServiceToken();
  const res = await fetch(
    `https://apps.carvanatech.com/qe/qeorchestration/api/v1/trades/extendorexpiretradein?acquisitionId=${acquisitionId}&daysToAdd=${daysToAdd}`,
    {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': 'application/json'
      }
    }
  );
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to expire trade (id=${acquisitionId}): ${res.status} ${res.statusText} — ${body}`
    );
  }
}

async function createSoftPullDirect(
  userId: string,
  email: string,
  persona: typeof SOFTPULL_FAIR_CREDIT,
  coApplicant?: typeof SOFTPULL_COSIGNER
): Promise<void> {
  const res = await fetch(
    'https://apik.carvanatech.com/oec/checkoutsoftpull/api/v1/softpull',
    {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-cvna-finx-clientsource': 'softpullMfe',
        'x-cvna-platform': 'Web',
        'x-cvna-softpull-source': 'FLP'
      },
      body: JSON.stringify({
        applicant: {
          address: {
            city: persona.address.city,
            state: persona.address.state,
            street: persona.address.street,
            zip: persona.address.zip.substring(0, 5)
          },
          dateOfBirth: toSoftPullDOB(persona.dateOfBirth),
          email,
          employmentStatus: 'Unaffected',
          firstName: persona.firstName,
          income: persona.income,
          lastName: persona.lastName,
          middleName: null,
          phoneNumber: toFormattedPhone(persona.phoneNumber),
          ssn: persona.ssn,
          statedEmploymentLength: 'OVER_FIVE_YEARS',
          suffix: null,
          userId
        },
        ...(coApplicant && {
          coapplicant: {
            address: {
              city: coApplicant.address.city,
              state: coApplicant.address.state,
              street: coApplicant.address.street,
              zip: coApplicant.address.zip.substring(0, 5)
            },
            dateOfBirth: toSoftPullDOB(coApplicant.dateOfBirth),
            email: `${coApplicant.address.state}_${coApplicant.firstName}_${
              coApplicant.lastName
            }_${Date.now()}@cosigner.com`,
            employmentStatus: 'Unaffected',
            firstName: coApplicant.firstName,
            income: coApplicant.income,
            lastName: coApplicant.lastName,
            middleName: null,
            phoneNumber: toFormattedPhone(coApplicant.phoneNumber),
            ssn: coApplicant.ssn,
            statedEmploymentLength: coApplicant.statedEmploymentLength,
            suffix: null
          }
        }),
        creationSource: 'FLP',
        leadSource: ''
      })
    }
  );
  if (!res.ok) {
    const errorBody = await res.text().catch(() => '');
    throw new Error(
      `Failed to submit soft pull: ${res.statusText} - ${errorBody}`
    );
  }
  const body = (await res.json()) as {
    applicant: { action: string; validationMessages: string[] };
  };
  const { action, validationMessages } = body.applicant;
  if (action === 'RequireSSN') {
    throw new Error(
      `Soft pull failed: Experian could not identify the user (RequireSSN): ${
        validationMessages?.join('; ') || 'no validation messages'
      }`
    );
  }
}

function requiresValidationToken(result: {
  errors?: Array<{ path?: string[] }>;
}): boolean {
  return Boolean(
    result.errors?.some(error => error.path?.includes('createAppraisal'))
  );
}

async function createTradeWithGraphQLDirect(
  zip: string,
  options: {
    hasLien?: boolean;
    lienHolder?: string;
    loanAmount?: number;
    vin?: string;
  } = {}
): Promise<string> {
  const defaultVin = '2GNALBEKXF1102556';
  const vin = options.vin || defaultVin;
  const hasLien = options.hasLien ?? true;
  const loanAmount = options.loanAmount ?? 0;
  const lienHolder = options.lienHolder ?? 'Test Bank';
  const useDefaultConfig = vin === defaultVin;

  const mutation = `
    mutation submitAppraisal(
      $vehicleVinInput: CreateVehicleVinInput!
      $plateStateInput: SetPlateStateInput!
      $odometerInput: SetOdometerInput!
      $zipCodeInput: SetZipCodeInput!
      $drivingMilesToHubInput: SetDrivingMilesToHubInput!
      $vehicleColorInput: VehicleColorInput!
      $powertrainInput: SetPowertrainInput!
      $premiumOptionsInput: SetPremiumOptionsInput!
      $previousAppraisalInput: SetPreviousAppraisalInput!
      $vehicleQuestionsInput: SetVehicleQuestionsInput!
      $leadInput: AppraisalLeadInput!
    ) {
      createVehicleByVin(input: $vehicleVinInput) { value }
      setPlateState(input: $plateStateInput) { value }
      setOdometer(input: $odometerInput) { value }
      setZipCode(input: $zipCodeInput) { value }
      setDrivingMilesToHub(input: $drivingMilesToHubInput) { value }
      setVehicleColor(input: $vehicleColorInput) { value }
      setPowertrain(input: $powertrainInput) { value }
      setPremiumOptions(input: $premiumOptionsInput) { value }
      setVehicleQuestions(input: $vehicleQuestionsInput) { value }
      setAuthenticatedAppraisalLead(input: $leadInput) { value }
      setPreviousAppraisal(input: $previousAppraisalInput) { value }
      createAppraisal { appraisalId }
    }
  `;

  const variables = {
    vehicleVinInput: {
      vin,
      configuration: useDefaultConfig
        ? {
            descriptionId: '12171',
            make: 'Chevrolet',
            model: 'Equinox',
            trim: 'LT Sport Utility 4D',
            year: 2015
          }
        : null
    },
    plateStateInput: {},
    odometerInput: { odometer: 150000 },
    zipCodeInput: { zipCode: zip },
    drivingMilesToHubInput: { drivingMiles: 26 },
    vehicleColorInput: { colorId: 2 },
    powertrainInput: {
      drivetrain: 5924385,
      engine: 5924344,
      transmission: 5924381
    },
    premiumOptionsInput: { options: [] },
    vehicleQuestionsInput: {
      accidentCount: 'NONE',
      aftermarketParts: false,
      drivable: true,
      engineIssues: [],
      estimatedRepairCost: null,
      exteriorDamages: [],
      interiorDamages: [],
      keyCount: 'ONE',
      lien: hasLien ? 'LOAN' : 'NONE',
      lienHolder: hasLien
        ? { balance: loanAmount, holder: lienHolder, monthlyPayment: 250 }
        : undefined,
      mechanicalDefects: [],
      physicalCondition: 'GREAT',
      roofCondition: 'GREAT',
      smokedIn: false,
      tireCondition: 'NONE',
      tradeIntent: 'TRADE_IN',
      windshieldCondition: 'NO_WINDSHIELD_DAMAGE'
    },
    leadInput: { browserCookieId: 'test-browser-cookie-id', leadSourceId: 0 },
    previousAppraisalInput: {}
  };

  type AppraisalResult = {
    data?: { createAppraisal?: { appraisalId?: string } };
    errors?: Array<{ path?: string[] }>;
  };

  const submitAppraisal = async (
    validationToken?: string
  ): Promise<AppraisalResult> => {
    const res = await fetch('https://apik.carvanatech.com/stc/offer/graphql/', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(validationToken && { 'x-validation-token': validationToken })
      },
      body: JSON.stringify({ query: mutation, variables })
    });

    return res.json();
  };

  let result = await submitAppraisal();
  if (requiresValidationToken(result)) {
    result = await submitAppraisal(await getValidationToken());
  }
  if (result.errors)
    throw new Error(`GraphQL errors: ${JSON.stringify(result.errors)}`);

  const acquisitionId = result.data?.createAppraisal?.appraisalId;
  if (!acquisitionId)
    throw new Error('No appraisal ID returned from GraphQL mutation');

  return acquisitionId;
}

// Adds a softpull to the currently logged-in user (for testazure).
// Requires the user to have already created a Common User and logged in.
async function createSoftPullUserDirect(
  creditStatus: 'Fair' | 'Excellent',
  withTrade = false
): Promise<CreateSoftPullUserResponse> {
  const persona =
    creditStatus === 'Excellent'
      ? SOFTPULL_EXCELLENT_CREDIT
      : SOFTPULL_FAIR_CREDIT;

  const sessionUser = getCurrentSessionUser();
  if (!sessionUser?.userId || !sessionUser?.email) {
    throw new Error(
      'No active session found. Please create a Common User and log in first, then try again.'
    );
  }

  let acquisitionId: string | undefined;
  if (withTrade) {
    const zip = sessionUser.address?.zip ?? persona.address.zip;
    acquisitionId = await createTradeWithGraphQLDirect(zip, { hasLien: false });
  }

  await createSoftPullDirect(sessionUser.userId, sessionUser.email, persona);

  return {
    acquisitionId,
    address: sessionUser.address ?? persona.address,
    dateOfBirth: persona.dateOfBirth,
    email: sessionUser.email,
    firstName: persona.firstName,
    income: persona.income,
    lastName: persona.lastName,
    password: sessionUser.password,
    phoneNumber: persona.phoneNumber,
    ssn: persona.ssn,
    userId: sessionUser.userId
  };
}

// ─── Unified API functions (route to direct or server-proxy by environment) ──

async function extendOrExpireTrade(
  acquisitionId: string,
  daysToAdd: number
): Promise<void> {
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      if (isLocalDev()) {
        const response = await fetch('/tools/test/expire-trade', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ acquisitionId, daysToAdd })
        });
        if (!response.ok) {
          const body = await response.text().catch(() => '');
          throw new Error(
            `Failed to expire trade: ${response.status} ${response.statusText} - ${body}`
          );
        }
      } else {
        await extendOrExpireTradeDirect(acquisitionId, daysToAdd);
      }

      return;
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }
}

export async function expireMostRecentTradeForCurrentUser(): Promise<{
  acquisitionId?: string;
  externalId?: string;
}> {
  const sessionUser = getCurrentSessionUser();
  if (!sessionUser?.userId) {
    throw new Error(
      'No active session found. Create a Common User and log in first.'
    );
  }
  const res = await fetch('/tools/test/expire-trade', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      daysToAdd: DAYS_TO_EXPIRE_TRADE,
      userId: sessionUser.userId
    })
  });
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(
      `Failed to expire trade: ${res.status} ${res.statusText} — ${body}`
    );
  }

  return (await res.json()) as { acquisitionId?: string; externalId?: string };
}

async function expireSoftPull(userId: string): Promise<void> {
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      if (isLocalDev()) {
        const expireRes = await fetch('/tools/test/expire-softpull', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ userId })
        });
        if (!expireRes.ok) {
          const body = await expireRes.text().catch(() => '');
          throw new Error(
            `Failed to expire softpull: ${expireRes.status} ${expireRes.statusText} — ${body}`
          );
        }
      } else {
        await expireSoftPullDirect(userId);
      }

      return;
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }
}

async function createSoftPullUser(
  creditStatus: 'Fair' | 'Excellent',
  withTrade = false
): Promise<CreateSoftPullUserResponse> {
  const minFicoScore = creditStatus === 'Excellent' ? 750 : 300;
  let lastError: Error | null = null;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      if (isLocalDev()) {
        const res = await fetch('/tools/test/create-softpull-user', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ minFicoScore, withTrade })
        });
        if (!res.ok) {
          const body = (await res.json()) as { error?: string };
          throw new Error(
            body.error || `Softpull user creation failed: ${res.statusText}`
          );
        }

        return (await res.json()) as CreateSoftPullUserResponse;
      } else {
        return await createSoftPullUserDirect(creditStatus, withTrade);
      }
    } catch (error) {
      lastError = error as Error;
      if (attempt === 3) break;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }
  throw (
    lastError || new Error('Failed to create softpull user after 3 attempts')
  );
}

async function createTrade(
  email: string,
  password: string,
  zip: string,
  options: {
    hasLien?: boolean;
    lienHolder?: string;
    loanAmount?: number;
    vin?: string;
  } = {}
): Promise<string> {
  let validationToken: string | undefined;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      if (isLocalDev()) {
        const res = await fetch('/tools/test/create-trade', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            email,
            hasLien: options.hasLien ?? true,
            lienHolder: options.lienHolder,
            loanAmount: options.loanAmount,
            password,
            validationToken,
            vin: options.vin,
            zip
          })
        });
        if (!res.ok) {
          const body = (await res.json()) as {
            error?: string;
            needsValidation?: boolean;
          };
          if (body.needsValidation && !validationToken) {
            validationToken = await getValidationToken();
            continue;
          }
          throw new Error(
            body.error || `Trade creation failed: ${res.statusText}`
          );
        }
        const data = (await res.json()) as { acquisitionId: string };
        if (!data.acquisitionId)
          throw new Error('No acquisition ID returned from trade creation');

        return data.acquisitionId;
      } else {
        return await createTradeWithGraphQLDirect(zip, options);
      }
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }
  throw new Error('Failed to create trade after 3 attempts');
}

// ─── Main createTestUser ─────────────────────────────────────────────────────

export async function createTestUser(
  userType: UserAccountType,
  options?: UserCreationOptions
): Promise<CreatedUser> {
  const state = options?.state || 'AZ';
  const name = options?.name;
  const password = `TestBot!${new Date().getFullYear()}`;

  if (
    userType === 'softpull' ||
    userType === 'soft-trade' ||
    userType === 'expired-softpull' ||
    userType === 'expired-soft-trade'
  ) {
    const creditStatus = options?.creditStatus || 'Excellent';
    const equityLevel = options?.equityLevel || 'Positive';
    const isNegativeEquity = equityLevel === 'Negative';
    const isSoftTrade =
      userType === 'soft-trade' || userType === 'expired-soft-trade';
    const withTrade = isSoftTrade && !isNegativeEquity;

    const softpullData = await createSoftPullUser(creditStatus, withTrade);

    const user: CreatedUser = {
      acquisitionId: withTrade ? softpullData.acquisitionId : undefined,
      address: softpullData.address,
      createdAt: new Date().toISOString(),
      dateOfBirth: softpullData.dateOfBirth,
      email: softpullData.email,
      firstName: softpullData.firstName,
      income: softpullData.income,
      lastName: softpullData.lastName,
      name,
      password: softpullData.password,
      phoneNumber: softpullData.phoneNumber,
      ssn: softpullData.ssn,
      type: userType,
      userId: softpullData.userId
    };

    if (isSoftTrade && isNegativeEquity) {
      const loanAmount = options?.loanAmount || 25000;
      const acquisitionId = await createTrade(
        softpullData.email,
        softpullData.password,
        softpullData.address.zip,
        {
          hasLien: true,
          lienHolder: options?.lienHolder,
          loanAmount,
          vin: options?.vin
        }
      );
      user.acquisitionId = acquisitionId;
      user.hasLoanAmount = true;
    }

    if (userType === 'expired-softpull' || userType === 'expired-soft-trade') {
      await expireSoftPull(user.userId!);
    }

    if (userType === 'expired-soft-trade' && user.acquisitionId) {
      await extendOrExpireTrade(user.acquisitionId, DAYS_TO_EXPIRE_TRADE);
    }

    storeTestUserInLocalStorage(user);

    return user;
  }

  if (userType === 'tradein' || userType === 'expired-trade') {
    const tradeEquityLevel = options?.equityLevel || 'Positive';
    const isNegative = tradeEquityLevel === 'Negative';

    const newUser: CreatedUser = {
      acquisitionId: '',
      address: { city: '', state, street: '', zip: '' },
      createdAt: new Date().toISOString(),
      dateOfBirth: '',
      email: '',
      firstName: '',
      income: 0,
      lastName: '',
      name,
      password,
      phoneNumber: '',
      ssn: '',
      type: userType,
      userId: ''
    };

    if (isNegative) {
      if (isLocalDev()) {
        const zip = state === 'AZ' ? '85001' : '02860';
        const city = state === 'AZ' ? 'Phoenix' : 'PAWTUCKET';
        const email = `${state}_MARSHALL_BROYLES_${Date.now()}@automate.com`;

        newUser.userId = await createUserAndGetId(email, password, zip);
        newUser.address = { city, state, street: '115 TRENTON ST APT 4', zip };
        newUser.dateOfBirth = '05291951';
        newUser.email = email;
        newUser.firstName = 'MARSHALL';
        newUser.income = 65342;
        newUser.lastName = 'BROYLES';
        newUser.phoneNumber = '2345678901';
        newUser.ssn = '666016662';

        const loanAmount = options?.loanAmount || 25000;
        const acquisitionId = await createTrade(email, password, zip, {
          hasLien: true,
          lienHolder: options?.lienHolder,
          loanAmount,
          vin: options?.vin
        });
        newUser.acquisitionId = acquisitionId;
        newUser.hasLoanAmount = true;
      } else {
        const sessionUser = getCurrentSessionUser();
        if (!sessionUser?.userId || !sessionUser?.email) {
          throw new Error(
            'No active session found. Please create a Common User and log in first, then try again.'
          );
        }
        const zip =
          sessionUser.address?.zip ?? (state === 'AZ' ? '85001' : '02860');
        const city =
          sessionUser.address?.city ??
          (state === 'AZ' ? 'Phoenix' : 'PAWTUCKET');

        newUser.userId = sessionUser.userId;
        newUser.address = sessionUser.address ?? {
          city,
          state,
          street: '115 TRENTON ST APT 4',
          zip
        };
        newUser.dateOfBirth = sessionUser.dateOfBirth ?? '05291951';
        newUser.email = sessionUser.email;
        newUser.firstName = sessionUser.firstName ?? 'MARSHALL';
        newUser.income = sessionUser.income ?? 65342;
        newUser.lastName = sessionUser.lastName ?? 'BROYLES';
        newUser.password = sessionUser.password;
        newUser.phoneNumber = sessionUser.phoneNumber ?? '2345678901';
        newUser.ssn = sessionUser.ssn ?? '666016662';

        const loanAmount = options?.loanAmount || 25000;
        const acquisitionId = await createTradeWithGraphQLDirect(zip, {
          hasLien: true,
          lienHolder: options?.lienHolder,
          loanAmount,
          vin: options?.vin
        });
        newUser.acquisitionId = acquisitionId;
        newUser.hasLoanAmount = true;
      }

      if (userType === 'expired-trade' && newUser.acquisitionId) {
        await extendOrExpireTrade(newUser.acquisitionId, DAYS_TO_EXPIRE_TRADE);
      }
    } else if (userType === 'expired-trade') {
      const res = await fetch('/tools/test/create-multiple-trade-user', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ trades: [{ type: 'expired' }] })
      });
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(
          `Failed to create expired trade: ${res.statusText} — ${body}`
        );
      }
      const data: CreateMultipleTradeUserResponse = await res.json();

      newUser.acquisitionId = data.acquisitionIds[0];
      newUser.address = data.address;
      newUser.dateOfBirth = data.dateOfBirth;
      newUser.email = data.email;
      newUser.firstName = data.firstName;
      newUser.income = data.income;
      newUser.lastName = data.lastName;
      newUser.password = data.password;
      newUser.phoneNumber = data.phoneNumber;
      newUser.ssn = data.ssn;
      newUser.userId = data.userId;

      storeTestUserInLocalStorage(newUser);

      return newUser;
    } else {
      // tradein positive equity
      if (isLocalDev()) {
        let lastError: Error | null = null;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            const res = await fetch('/tools/test/create-trade-user', {
              method: 'POST',
              headers: { 'content-type': 'application/json' }
            });
            if (!res.ok) {
              const body = (await res.json()) as { error?: string };
              throw new Error(
                body.error || `Trade user creation failed: ${res.statusText}`
              );
            }
            const data = (await res.json()) as CreateTradeUserResponse;
            newUser.acquisitionId = data.acquisitionId;
            newUser.address = data.address;
            newUser.dateOfBirth = data.dateOfBirth;
            newUser.email = data.email;
            newUser.firstName = data.firstName;
            newUser.income = data.income;
            newUser.lastName = data.lastName;
            newUser.password = data.password;
            newUser.phoneNumber = data.phoneNumber;
            newUser.ssn = data.ssn;
            newUser.userId = data.userId;
            break;
          } catch (error) {
            lastError = error as Error;
            if (attempt === 3) break;
            await sleep(Math.pow(2, attempt - 1) * 1000);
          }
        }
        if (lastError) throw lastError;
      } else {
        // testazure: use current session user, create trade via direct apik call
        const sessionUser = getCurrentSessionUser();
        if (!sessionUser?.userId || !sessionUser?.email) {
          throw new Error(
            'No active session found. Please create a Common User and log in first, then try again.'
          );
        }
        const zip =
          sessionUser.address?.zip ?? (state === 'AZ' ? '85001' : '02860');
        const city =
          sessionUser.address?.city ??
          (state === 'AZ' ? 'Phoenix' : 'PAWTUCKET');

        newUser.userId = sessionUser.userId;
        newUser.address = sessionUser.address ?? {
          city,
          state,
          street: '115 TRENTON ST APT 4',
          zip
        };
        newUser.dateOfBirth = sessionUser.dateOfBirth ?? '05291951';
        newUser.email = sessionUser.email;
        newUser.firstName = sessionUser.firstName ?? 'MARSHALL';
        newUser.income = sessionUser.income ?? 65342;
        newUser.lastName = sessionUser.lastName ?? 'BROYLES';
        newUser.password = sessionUser.password;
        newUser.phoneNumber = sessionUser.phoneNumber ?? '2345678901';
        newUser.ssn = sessionUser.ssn ?? '666016662';

        const acquisitionId = await createTradeWithGraphQLDirect(zip, {
          hasLien: false,
          vin: options?.vin
        });
        newUser.acquisitionId = acquisitionId;
      }
    }

    storeTestUserInLocalStorage(newUser);

    return newUser;
  }

  if (userType === 'multiple-trades') {
    const trades = options?.trades ?? [
      { type: 'active' as const },
      { type: 'expired' as const }
    ];

    const newUser: CreatedUser = {
      acquisitionId: '',
      acquisitionIds: [],
      address: { city: '', state, street: '', zip: '' },
      createdAt: new Date().toISOString(),
      dateOfBirth: '',
      email: '',
      firstName: '',
      income: 0,
      lastName: '',
      name,
      password,
      phoneNumber: '',
      ssn: '',
      type: userType,
      userId: ''
    };

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        const response = await fetch('/tools/test/create-multiple-trade-user', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ trades })
        });
        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(
            `Failed to create multiple trade user: ${response.statusText} - ${errorBody}`
          );
        }
        const data: CreateMultipleTradeUserResponse = await response.json();
        newUser.acquisitionId = data.acquisitionIds[0];
        newUser.acquisitionIds = data.acquisitionIds;
        newUser.address = data.address;
        newUser.dateOfBirth = data.dateOfBirth;
        newUser.email = data.email;
        newUser.firstName = data.firstName;
        newUser.income = data.income;
        newUser.lastName = data.lastName;
        newUser.password = data.password;
        newUser.phoneNumber = data.phoneNumber;
        newUser.ssn = data.ssn;
        newUser.userId = data.userId;
        break;
      } catch (error) {
        if (attempt === 3) throw error;
        await sleep(Math.pow(2, attempt - 1) * 1000);
      }
    }

    storeTestUserInLocalStorage(newUser);

    return newUser;
  }

  if (userType === 'common') {
    const zip = state === 'AZ' ? '85001' : '02860';
    const city = state === 'AZ' ? 'Phoenix' : 'PAWTUCKET';
    const email = `${state}_MARSHALL_BROYLES_${Date.now()}@automate.com`;

    const userId = await createUserAndGetId(email, password, zip);

    const newUser: CreatedUser = {
      address: { city, state, street: '115 TRENTON ST APT 4', zip },
      createdAt: new Date().toISOString(),
      dateOfBirth: '05291951',
      email,
      firstName: 'MARSHALL',
      income: 65342,
      lastName: 'BROYLES',
      name,
      password,
      phoneNumber: '2345678901',
      ssn: '666016662',
      type: userType,
      userId
    };

    storeTestUserInLocalStorage(newUser);

    return newUser;
  }

  if (userType === 'loan-amount' || userType === 'update-user-trade-info') {
    if (isLocalDev()) {
      const zip = state === 'AZ' ? '85001' : '02860';
      const city = state === 'AZ' ? 'Phoenix' : 'PAWTUCKET';
      const email = `${state}_MARSHALL_BROYLES_${Date.now()}@automate.com`;

      const userId = await createUserAndGetId(email, password, zip);

      const newUser: CreatedUser = {
        address: { city, state, street: '115 TRENTON ST APT 4', zip },
        createdAt: new Date().toISOString(),
        dateOfBirth: '05291951',
        email,
        firstName: 'MARSHALL',
        income: 65342,
        lastName: 'BROYLES',
        name,
        password,
        phoneNumber: '2345678901',
        ssn: '666016662',
        type: userType,
        userId
      };

      const loanAmount = options?.loanAmount || 25000;
      const acquisitionId = await createTrade(email, password, zip, {
        hasLien: true,
        lienHolder: options?.lienHolder,
        loanAmount,
        vin: options?.vin
      });
      newUser.acquisitionId = acquisitionId;
      newUser.hasLoanAmount = true;

      storeTestUserInLocalStorage(newUser);

      return newUser;
    } else {
      const sessionUser = getCurrentSessionUser();
      if (!sessionUser?.userId || !sessionUser?.email) {
        throw new Error(
          'No active session found. Please create a Common User and log in first, then try again.'
        );
      }
      const zip =
        sessionUser.address?.zip ?? (state === 'AZ' ? '85001' : '02860');
      const city =
        sessionUser.address?.city ?? (state === 'AZ' ? 'Phoenix' : 'PAWTUCKET');

      const newUser: CreatedUser = {
        address: sessionUser.address ?? {
          city,
          state,
          street: '115 TRENTON ST APT 4',
          zip
        },
        createdAt: new Date().toISOString(),
        dateOfBirth: sessionUser.dateOfBirth ?? '05291951',
        email: sessionUser.email,
        firstName: sessionUser.firstName ?? 'MARSHALL',
        income: sessionUser.income ?? 65342,
        lastName: sessionUser.lastName ?? 'BROYLES',
        name,
        password: sessionUser.password,
        phoneNumber: sessionUser.phoneNumber ?? '2345678901',
        ssn: sessionUser.ssn ?? '666016662',
        type: userType,
        userId: sessionUser.userId
      };

      const loanAmount = options?.loanAmount || 25000;
      const acquisitionId = await createTradeWithGraphQLDirect(zip, {
        hasLien: true,
        lienHolder: options?.lienHolder,
        loanAmount,
        vin: options?.vin
      });
      newUser.acquisitionId = acquisitionId;
      newUser.hasLoanAmount = true;

      storeTestUserInLocalStorage(newUser);

      return newUser;
    }
  }

  throw new Error(`User type not supported: ${userType}`);
}

export async function createCommonUser(
  name?: string,
  state = 'AZ'
): Promise<CreatedUser> {
  const zip = state === 'AZ' ? '85001' : '02860';
  const city = state === 'AZ' ? 'Phoenix' : 'PAWTUCKET';
  const password = `TestBot!${new Date().getFullYear()}`;
  const email = `${state}_MARSHALL_BROYLES_${Date.now()}@automate.com`;

  const createRes = await fetch('/v2/user-create', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      callerIdentifier: 'AccountCreationForm',
      confirmPassword: password,
      deliveryZip: zip,
      email,
      firstName: 'MARSHALL',
      isAgreeToTermsAndConditions: true,
      isOptedInToTcpaConsent: true,
      lastName: 'BROYLES',
      partnerId: '1',
      password,
      phoneNumber: toFormattedPhone('2345678901')
    })
  });
  if (!createRes.ok)
    throw new Error(`Failed to create user: ${createRes.statusText}`);

  const user: CreatedUser = {
    address: { city, state, street: '115 TRENTON ST APT 4', zip },
    createdAt: new Date().toISOString(),
    dateOfBirth: '05291951',
    email,
    firstName: 'MARSHALL',
    income: 65342,
    lastName: 'BROYLES',
    name,
    password,
    phoneNumber: '2345678901',
    ssn: '666016662',
    type: 'common'
  };
  storeTestUserInLocalStorage(user);

  return user;
}

export async function addSoftPullToCurrentUser(options: {
  creditStatus: 'Excellent' | 'Fair';
  expired?: boolean;
  withCoSigner?: boolean;
}): Promise<void> {
  const sessionUser = getCurrentSessionUser();
  if (!sessionUser?.userId || !sessionUser?.email) {
    throw new Error(
      'No active session found. Create a Common User and log in first.'
    );
  }
  const persona =
    options.creditStatus === 'Excellent'
      ? SOFTPULL_EXCELLENT_CREDIT
      : SOFTPULL_FAIR_CREDIT;
  const coApplicant = options.withCoSigner ? SOFTPULL_COSIGNER : undefined;

  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await createSoftPullDirect(
        sessionUser.userId,
        sessionUser.email,
        persona,
        coApplicant
      );
      break;
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }

  if (options.expired) {
    await expireSoftPull(sessionUser.userId);
  }
}

export async function loginUser(
  user: CreatedUser,
  options: { reload?: boolean } = {}
): Promise<CreatedUser> {
  sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));

  const response = await fetch('/v2/user-login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ emailAddress: user.email, password: user.password })
  });

  const result = (await response.json()) as {
    content?: { userId?: string };
    error?: string;
  };

  if (!response.ok) {
    throw new Error(
      `Login failed: ${response.statusText} - ${
        result.error ?? 'Unknown error'
      }`
    );
  }

  const userId = result.content?.userId;
  const updatedUser = userId ? { ...user, userId } : user;
  if (userId) {
    sessionStorage.setItem(SESSION_USER_KEY, JSON.stringify(updatedUser));
    storeTestUserInLocalStorage(updatedUser);
  }

  if (options.reload !== false) window.location.reload();

  return updatedUser;
}

export async function addTradeToCurrentUser(
  options: TradeOptions
): Promise<string> {
  const sessionUser = getCurrentSessionUser();
  if (!sessionUser?.userId || !sessionUser?.email) {
    throw new Error(
      'No active session found. Create a Common User and log in first.'
    );
  }
  const zip = sessionUser.address?.zip ?? '85001';
  const hasLien = options.equityLevel === 'Negative';
  const defaultVin =
    options.equityLevel === 'High Positive'
      ? '1GYS4EKL2PR191194' // 2023 Cadillac Escalade
      : '2GNALBEKXF1102556'; // 2015 Chevrolet Equinox

  let acquisitionId = '';
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      acquisitionId = await createTradeWithGraphQLDirect(zip, {
        hasLien,
        loanAmount: hasLien ? options.loanAmount ?? 25000 : undefined,
        vin: options.vin ?? defaultVin
      });
      break;
    } catch (error) {
      if (attempt === 3) throw error;
      await sleep(Math.pow(2, attempt - 1) * 1000);
    }
  }

  return acquisitionId;
}

export async function logoutUser(
  options: { reload?: boolean } = {}
): Promise<void> {
  sessionStorage.removeItem(SESSION_USER_KEY);
  try {
    await fetch('/user-logout', {
      method: 'POST',
      mode: 'cors',
      headers: { 'content-type': 'application/json' }
    });
  } catch {
    // ignore — still reload even if the logout API is unavailable
  }
  if (options.reload !== false) window.location.reload();
}
